import { MaterialLearningGroupType } from "@/models/material.model";
import { QueryParams } from "@/models/query-params.model";
import { Status } from "@/models/stats.model";
import { ChaptersRest } from "@/services/rest/chapters.rest";
import { ClassesRest } from "@/services/rest/classes.rest";
import { CoursesRest } from "@/services/rest/courses.rest";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { BehaviorSubject, Observable, map, take } from "rxjs";

export class ChaptersService {
  constructor(
    private _chapters = new ChaptersRest(),
    private _materials = new MaterialsRest(),
    private _generics = new GenericsUtils(),
    private _courses = new CoursesRest(),
    private _classes = new ClassesRest()
  ) {}

  private chapters$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  chapters: Observable<any> = this.chapters$.asObservable();
  private chapter$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  chapter: Observable<any> = this.chapter$.asObservable();
  private material$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  material: Observable<any> = this.material$.asObservable();
  private courses$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  courses: Observable<any> = this.courses$.asObservable();
  private classes$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  classes: Observable<any> = this.classes$.asObservable();

  getChaptersByCourse(courseId: string, params?: QueryParams | any): void {
    this._chapters
      .getChaptersByCourse(courseId, params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.chapters$.next(response);
        },
      });
  }

  getCourseTraditional(courseId: string): void {
    this._courses
      .getCourseTraditional(courseId)
      .pipe(
        take(1),
        map((e) => {
          return e.chapters;
        })
      )
      .subscribe({
        next: (response) => {
          this.chapters$.next(response);
        },
      });
  }

  getMaterialById(trailId: string, materialId: string): void {
    this._materials
      .getMaterialById(trailId, materialId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.material$.next(response);
        },
      });
  }

  getMaterialGroupName(status: string): string {
    if (status == MaterialLearningGroupType.INTRODUCTION) {
      status = GenericsUtils.translateI18n("objects.type.intro");
    } else if (status == MaterialLearningGroupType.MOTIVATION) {
      status = GenericsUtils.translateI18n("objects.type.motivation");
    } else if (status == MaterialLearningGroupType.DEVELOPMENT) {
      status = GenericsUtils.translateI18n("objects.type.dev");
    } else if (status == MaterialLearningGroupType.CLOSURE) {
      status = GenericsUtils.translateI18n("objects.type.closure");
    }
    return status;
  }

  getMaterialType(type: string): string {
    if (type == "video" || type == "application/x-mpegURL") {
      type = "md-ondemandvideo";
    } else if (
      type == "image/png" ||
      type == "image/jpg" ||
      type == "image/jpeg"
    ) {
      type = "md-image";
    } else if (type == "application/pdf") {
      type = "md-pictureaspdf";
    } else {
      type = "md-filecopy-round";
    }
    return type;
  }

  getObjectsByLearningObjective(objective: string, params: QueryParams): void {
    this._materials
      .getObjectsByLearningObjective(objective, params)
      .pipe(
        map((e) => {
          e.content?.forEach((material) => {
            material.file.url = this._generics.getSecureUrl(
              material?.file?.url
            );
            material.file.mimetype = this._generics.getMimeType(
              material?.file?.mimetype
            );
          });
          return e;
        })
      )
      .subscribe({
        next: (response) => {
          this.material$.next(response);
        },
      });
  }

  getAllClasses(params: QueryParams): void {
    this._classes
      .getAllClasses(params)
      .pipe(
        map((e) => {
          e.content = e.content.map((item: any) => {
            return {
              id: item.id,
              name: item.name,
              studentsAverage: item.users?.length,
              coursesAverage: item.coursesIds?.length,
              status: this.remakeObjectStatus(
                item.disabled ? Status.INACTIVE : Status.ACTIVE
              ),
            };
          });
          return e;
        })
      )
      .subscribe({
        next: (response) => {
          this.classes$.next(response);
        },
      });
  }
  getStatus(status: string): string {
    if (status == Status.ACTIVE) {
      status = GenericsUtils.translateI18n("classes.status.enabled");
    } else if (status == Status.INACTIVE) {
      status = GenericsUtils.translateI18n("classes.status.disabled");
    }
    return status;
  }

  getStatusColor(status: string): string {
    if (status == Status.INACTIVE) {
      status = "bg-h-secondary-200 text-yellow-900";
    } else if (status == Status.ACTIVE) {
      status = "bg-h-success-100 text-green-900";
    }
    return status;
  }

  remakeObjectStatus(item: any): any {
    var status = { name: "", class: "", value: "" };
    status.name = this.getStatus(item);
    status.class = this.getStatusColor(item);
    status.value = item;
    return status;
  }

}
